/**
 * InvoiceClosureSettingsSection Component
 *
 * When an invoice counts as completed, and — in a section of its own — whether and when a
 * completed one is archived. Stored the moment a choice changes.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import {
  DEFAULT_INVOICE_COMPLETION,
  Permission,
  type InvoiceCompletionSettings
} from '@insio/shared-types';
import { pick } from 'es-toolkit';
import { AlertBanner, Loader, Paper } from '@insio/ui-toolkit';
import { usePermissions } from '@/features/auth';
import {
  HasTargetConnectionDocument,
  InvoiceAutomationSettingsDocument,
  UpdateInvoiceAutomationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { ArchivingOnCompletionSection } from './ArchivingOnCompletionSection';
import { CompletionConditionToggle } from './CompletionConditionToggle';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

export function InvoiceClosureSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.invoiceClosure');
  const { showError } = useToastHelpers();
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(InvoiceAutomationSettingsDocument);
  // The response carries no id to normalise, so the settings query is refetched.
  const [updateSettings] = useMutation(UpdateInvoiceAutomationSettingsDocument, {
    refetchQueries: [{ query: InvoiceAutomationSettingsDocument }],
    awaitRefetchQueries: true
  });

  const [draft, setDraft] = useState<InvoiceCompletionSettings>(DEFAULT_INVOICE_COMPLETION);

  const settings = data?.invoiceAutomationSettings;

  // Posting is learnt from a connected accounting system: asked only while it is a condition, and
  // only by whoever may read connections. `no-cache`: a cached one-edge page would cut the list.
  const { hasPermission } = usePermissions();
  const canReadConnections = hasPermission(Permission.INTEGRATIONS_MANAGE);
  const { data: connectionsData } = useQuery(HasTargetConnectionDocument, {
    skip: !draft.isPostingRequired || !canReadConnections,
    fetchPolicy: 'no-cache'
  });
  const hasNoAccountingSystem = connectionsData?.targetConnections.edges.length === 0;

  useEffect(() => {
    if (settings) {
      setDraft({
        isApprovalRequired: settings.isApprovalRequired,
        isPaymentRequired: settings.isPaymentRequired,
        isPostingRequired: settings.isPostingRequired,
        isArchivingOnCompletion: settings.isArchivingOnCompletion,
        archiveAfterDays: settings.archiveAfterDays
      });
    }
  }, [settings]);

  /** Stored the moment it changes, one key at a time; a refused write puts that key back. */
  const handleChange = async (change: Partial<InvoiceCompletionSettings>) => {
    if (!canChange) return;
    const reverted = pick(draft, Object.keys(change) as (keyof InvoiceCompletionSettings)[]);
    setDraft(current => ({ ...current, ...change }));
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      // Approval cannot be switched off in this version, so every save restates it.
      await updateSettings({
        variables: { input: { ...change, isApprovalRequired: true } },
        errorPolicy: 'none'
      });
    } catch {
      setDraft(current => ({ ...current, ...reverted }));
      showError(t('saveError'));
    }
  };

  if (loading && !settings) {
    return <Loader.Section />;
  }

  if (error) {
    return <AlertBanner variant='error' title={t('loadError')} />;
  }

  return (
    <div className='space-y-lg'>
      <Paper heading='stripe' bleed padding='none' testId='invoice-closure-conditions-section'>
        <Paper.Title>
          <h2>{t('title')}</h2>
        </Paper.Title>
        <Paper.Content className='space-y-md'>
          <p className='text-sm text-foreground-muted'>{t('description')}</p>
          <div className='space-y-md'>
            <CompletionConditionToggle
              label={t('approval.label')}
              checked
              disabled
              onChange={() => undefined}
              testId='invoice-closure-approval'
            />
            <CompletionConditionToggle
              label={t('payment.label')}
              checked={draft.isPaymentRequired}
              onChange={isPaymentRequired => handleChange({ isPaymentRequired })}
              disabled={!canChange}
              testId='invoice-closure-payment'
            />
            <div className='space-y-sm'>
              <CompletionConditionToggle
                label={t('posting.label')}
                checked={draft.isPostingRequired}
                onChange={isPostingRequired => handleChange({ isPostingRequired })}
                disabled={!canChange}
                testId='invoice-closure-posting'
              />
              {draft.isPostingRequired && hasNoAccountingSystem && (
                <AlertBanner
                  variant='warning'
                  title={t('posting.noAccountingSystem')}
                  testId='invoice-closure-posting-no-accounting-system'
                />
              )}
            </div>
          </div>
        </Paper.Content>
      </Paper>

      <ArchivingOnCompletionSection
        namespace='invoiceClosure'
        checked={draft.isArchivingOnCompletion}
        onChange={isArchivingOnCompletion => handleChange({ isArchivingOnCompletion })}
        archiveAfterDays={draft.archiveAfterDays}
        onArchiveAfterDaysChange={archiveAfterDays => handleChange({ archiveAfterDays })}
        disabled={!canChange}
        testIdPrefix='invoice-closure'
      />
    </div>
  );
}
